// ─────────────────────────────────────────────────────────────────────────────
// ServiceDetailPage.jsx — /services/:slug
// MIAVIT individual service page style
//
// Structure:
//   1. Full-width hero image
//   2. PageHero with breadcrumbs
//   3. 2-col: title + description LEFT | green "Why..." checklist RIGHT
//   4. CtaBanner
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { services, getServiceBySlug } from '@data/services'
import PageHero from '@components/common/PageHero'
import CtaBanner from '@components/common/CtaBanner'
import SEOHead from '@components/common/SEOHead'

// Green check icon (on white bg)
const CheckGreen = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="10" cy="10" r="10" fill="#007935" />
        <path d="M5.5 10L8.5 13L14.5 7"
            stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const BG = 'linear-gradient(135deg, #2d5c3e 0%, #3d7a55 100%)'

export default function ServiceDetailPage() {
    const { slug } = useParams()
    const service = getServiceBySlug(slug)

    useEffect(() => { window.scrollTo(0, 0) }, [slug])

    if (!service) return <Navigate to="/services" replace />

    // Other services for cross-links
    const others = services.filter(s => s.id !== service.id)

    return (
        <main>
            <SEOHead
                title={service.name}
                description={service.description}
                path={`/services/${service.slug}`}
            />
            {/* ── PageHero with breadcrumbs ── */}
            <PageHero
                title={service.shortName}
                breadcrumbs={[
                    { label: 'Home', to: '/' },
                    { label: 'Services', to: '/services' },
                    { label: service.shortName },
                ]}
            />

            {/* ── Full-width hero image ── */}
            <div style={{
                width: '100%',
                height: 'clamp(200px, 35vw, 400px)',
                overflow: 'hidden',
                position: 'relative',
                background: BG,
            }}>
                {service.image ? (
                    <img src={service.image} alt={service.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 20px)',
                    }} />
                )}
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 60%)',
                }} />
            </div>


            {/* ── Main 2-col section — MIAVIT exact layout ── */}
            <section style={{ background: '#ffffff', padding: '64px 0 72px' }}>
                <div style={{
                    maxWidth: 'var(--container-max)', margin: '0 auto',
                    padding: '0 var(--container-padding)',
                }}>
                    <div
                        className="service-detail-grid"
                        style={{
                            maxWidth: '760px',
                            margin: '0 auto',
                        }}
                    >

                        {/* LEFT: title + tagline + description */}
                        <div>
                            {/* Large light-weight heading — MIAVIT style */}
                            <h1 style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 'clamp(1.75rem, 3.5vw, 3rem)',
                                fontWeight: 300,
                                letterSpacing: '0.02em',
                                color: '#007935',
                                lineHeight: 1.15,
                                margin: '0 0 14px',
                            }}>
                                {service.name}
                            </h1>

                            {/* Tagline */}
                            <p style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '1.0625rem', fontWeight: 400,
                                color: '#55544e', fontStyle: 'italic',
                                lineHeight: 1.6, margin: '0 0 24px',
                            }}>
                                {service.tagline}
                            </p>

                            {/* Why checklist (moved from right panel) */}
                            <div style={{ marginBottom: '32px' }}>
                                <p style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '11px', fontWeight: 700,
                                    letterSpacing: '0.10em', textTransform: 'uppercase',
                                    color: '#007935', margin: '0 0 14px',
                                }}>
                                    Why Salient {service.shortName}?
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {service.why.map((point, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                            <CheckGreen />
                                            <span style={{
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontSize: '14px', color: '#55544e',
                                                lineHeight: 1.55,
                                            }}>
                                                {point}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Delivery methods */}
                            <div style={{
                                padding: '20px 24px',
                                background: '#f5f5f3',
                                border: '1px solid #e8e8e8',
                                marginBottom: '28px',
                            }}>
                                <p style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '11px', fontWeight: 700,
                                    letterSpacing: '0.10em', textTransform: 'uppercase',
                                    color: '#007935', margin: '0 0 12px',
                                }}>
                                    How it's delivered
                                </p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {service.deliveryMethods.map(m => (
                                        <div key={m} style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '6px 12px',
                                            background: '#ffffff', border: '1px solid #e0dfda',
                                        }}>
                                            <CheckGreen />
                                            <span style={{
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontSize: '13px', fontWeight: 500,
                                                color: '#44443e',
                                            }}>
                                                {m}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Other services quick links */}
                            {others.length > 0 && (
                                <div>
                                    <p style={{
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontSize: '11px', fontWeight: 700,
                                        letterSpacing: '0.10em', textTransform: 'uppercase',
                                        color: '#88877f', margin: '0 0 12px',
                                    }}>
                                        Other Services
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {others.map(s => (
                                            <a
                                                key={s.id}
                                                href={`/services/${s.slug}`}
                                                style={{
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontSize: '13.5px', fontWeight: 500,
                                                    color: '#007935', textDecoration: 'none',
                                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                    transition: 'opacity 0.2s ease',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                                                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                                {s.shortName}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>


                    </div>
                </div>
            </section>

            {/* CtaBanner */}
            <CtaBanner
                tag="Contact"
                title="Interested in this service?"
                body="Get in touch with our technical team to discuss how we can support your operation with expert nutritional guidance and responsive service."
                buttonText="Contact us"
                buttonTo="/contact"
            />

        </main>
    )
}